/**
 * AffirmService.js
 * Service for handling Affirm payment integration
 */

import config from '../configuration/config';

/**
 * Check if Affirm is enabled in the current environment
 */
export const isAffirmEnabled = () => {
  return config.affirm && config.affirm.key && config.affirm.url && config.affirm.active;
};

/**
 * Initializes Affirm with the configuration from the environment
 */

export const initializeAffirm = () => {
  if (!isAffirmEnabled()) {
    return false;
  }

  // Initialize Affirm
  var _affirm_config = {
    public_api_key: config.affirm.key,
    script: config.affirm.url,
    locale: "en_US",
    country_code: "USA",
  };

  (function(m,g,n,d,a,e,h,c){var b=m[n]||{},k=document.createElement(e),p=document.getElementsByTagName(e)[0],l=function(a,b,c){return function(){a[b]._.push([c,arguments])}};b[d]=l(b,d,"set");var f=b[d];b[a]={};b[a]._=[];f._=[];b._=[];b[a][h]=l(b,a,h);b[c]=function(){b._.push([h,arguments])};a=0;for(c="set add save post open empty reset on off trigger ready setProduct".split(" ");a<c.length;a++)f[c[a]]=l(b,d,c[a]);a=0;for(c=["get","token","url","items"];a<c.length;a++)f[c[a]]=function(){};k.async=!0;k.src=g[e];p.parentNode.insertBefore(k,p);delete g[e];f(g);m[n]=b})(window,_affirm_config,"affirm","checkout","ui","script","ready","jsReady");
  
  return true;
};

/**
 * Format date string from "Month Day, Year" to "YYYY-MM-DDTHH:MM" format
 * @param {string} dateStr - Date string to format
 * @returns {string|null} Formatted date string or null if invalid
 */
export const formatDateString = (dateStr) => {
  if (!dateStr) return null;
  
  try {
    const date = new Date(dateStr);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date:", dateStr);
      return null;
    }
    
    // Format to YYYY-MM-DDTHH:MM
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (e) {
    console.error("Error formatting date:", e);
    return null;
  }
};

/**
 * Convert dollar amount to cents for Affirm
 * @param {number} amount - Amount in dollars
 * @returns {number} Amount in cents
 */
export const toCent = (amount) => {
  return Math.round(amount * 100);
};

/**
 * Create an itinerary object for Affirm checkout
 * @param {object} trip - Trip object
 * @param {object} user - User object
 * @returns {object} Affirm itinerary object
 */
export const createAffirmItinerary = (trip, user) => {
  let itinerary = {
    type: "event",
    sku: trip.id,
    display_name: trip.name || trip.displayName,
    location: trip.destination,
    date_start: formatDateString(trip.startDate),
    date_end: formatDateString(trip.endDate),
    travelers: {
      0: {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        phone: user.phone
      }
    }
  };
  
  // Add any add-ons as separate items
  if (trip && trip.currentAddOns && trip.currentAddOns.length > 0) {
    let addOns = {};
    trip.currentAddOns.forEach(addon => {
      addOns[addon.name] = addon.unitPrice;
    });
    itinerary["add-ons"] = addOns;
  }
  
  return itinerary;
};

/**
 * Open Affirm learn more modal
 * @param {number} paymentAmount - Payment amount in dollars
 */
export const openAffirmLearnMore = (paymentAmount) => {
  if (window.affirm && window.affirm.ui) {
    // Open Affirm promotional modal with the current payment amount
    window.affirm.ui.modal.open({
      amount: toCent(paymentAmount),
      pageType: "homepage",
      onFail: function(error) {
        console.error("Affirm modal failed to open:", error);
      }
    });
  } else {
    console.error("Affirm is not properly initialized");
  }
};

/**
 * Handle Affirm checkout process
 * @param {object} params - Parameters for checkout
 * @param {object} params.trip - Trip object
 * @param {object} params.user - User object
 * @param {number} params.totalAmountDue - Total amount due in dollars
 * @param {function} params.onSuccess - Success callback
 * @param {function} params.onFail - Failure callback
 */
export const handleAffirmCheckout = ({ trip, user, totalAmountDue, onSuccess, onFail }) => {
  if (!window.affirm || !window.affirm.checkout) {
    console.error("Affirm is not properly initialized");
    if (onFail) {
      onFail("Affirm is not properly initialized");
    }
    return;
  }
  
  if (!totalAmountDue || totalAmountDue <= 0) {
    if (onFail) {
      onFail("No payment amount due");
    }
    return;
  }
      
  // Prepare checkout object with only the essential required fields
  const checkoutObject = {
    merchant: {
      user_confirmation_url: window.location.href,
      user_cancel_url: window.location.href,
      public_key: config.affirm.key,
      name: "Growth International Volunteer Excursions (GIVE)"
    },
    billing: {
      name: {
        first: user.firstName || "",
        last: user.lastName || ""
      },
      address: {
        line1: user.mailingStreet || "",
        line2: user.address2 || "",
        city: user.mailingCity || "",
        state: user.mailingState || "",
        zipcode: user.mailingPostalCode || "",
        country: user.mailingCountry || "USA"
      },
      phone_number: user.phone || "",
      email: user.email
    },
    items: [
      {
        display_name: trip ? (trip.name || trip.displayName || "GIVE Trip Payment") : "GIVE Trip Payment",
        sku: trip ? trip.id : `GIVE-${Date.now()}`,
        unit_price: toCent(totalAmountDue),
        qty: 1
      }
    ],
    itinerary: createAffirmItinerary(trip, user),
    total: toCent(totalAmountDue)
  };
      
  // Start Affirm checkout with the object
  window.affirm.checkout(checkoutObject);
  
  // Handle checkout completion
  window.affirm.checkout.open({
    onFail: (error) => {
      console.error("Affirm checkout failed:", error);
      if (onFail) {
        onFail(error);
      }
    },
    onSuccess: (result) => {
      console.log("Affirm checkout successful, token:", result.checkout_token);
      if (onSuccess) {
        onSuccess(result);
      }
    }
  });
};

export default {
  initializeAffirm,
  isAffirmEnabled,
  formatDateString,
  toCent,
  createAffirmItinerary,
  openAffirmLearnMore,
  handleAffirmCheckout
};
